import { AnalyticsPayload, renderTemplate, SB } from '@play-co/replicant';
import type { ChatbotAssets } from '@play-co/replicant';
import {
  ChatbotMessageInstance,
  TemplateRendererAPI,
} from '@play-co/replicant';
import {
  AlternativeOpenWebBuckets,
  getLineRedirectURL,
  getOpenWebAppLink,
} from 'src/replicant/utils/payload';
import {
  androidGenericTemplate,
  chatbotAssets,
  ChatbotMessagePayload,
  chatbotMessageTemplates,
  iosGenericTemplate,
} from '../messageTemplates';
import { FlexMessagesArgs, FlexMessagesTemplate } from './index';
import {
  Action,
  FlexBubble,
  FlexCarousel,
  FlexContainer,
  FlexMessage,
} from './types';
import { getRandomItemFromArray } from 'src/replicant/utils/random';

export type FlexCreativeAsset = {
  id: string;
  image: string;
  noCTA?: boolean; // If we want to disable CTA for this creative
};

export type FlexCreativeText = {
  id: string;
  text: string;
  cta: string;
};

/**
 * Action argument for single message
 */
export type ChatbotFlexMessageArgs = {
  template: FlexMessagesTemplate;
  payload: ChatbotMessagePayload;
  data: FlexMessagesArgs[FlexMessagesTemplate];
  analyticsUserProperties?: {
    [key: string]: unknown;
  };
};

// This trick will make type checker happy and will allow get all types in place
type ChatbotFlexAssets = typeof chatbotAssets | ChatbotAssets;

/**
 * This argument will be sent to render function
 */
export type FlexMessageTemplate<T> = (
  data: T,
  payload: AnalyticsPayload & {
    alternativeWebIABBucket: AlternativeOpenWebBuckets;
  },
  api: TemplateRendererAPI<ChatbotFlexAssets>,
) => FlexMessage;

/**
 * Use this function for creating other messages templates
 *
 * @example
 * export const demoMessages = {
 *    id_of_message_template: createFlexMessageTemplate(SB.object({}) ,
 *      (data: typeof SB.object({}), payload: AnalyticsPayload, api: TemplateRendererAPI) => {
 *        return '{ ...flex template... }';
 *      }
 * )};
 */
export function createFlexMessageTemplate<T>(
  templateData: SB.Schema<T>,
  flexMessageTemplateGenerator: FlexMessageTemplate<T>,
) {
  return renderTemplate({
    args: templateData,
    renderers: {
      line: ({ api, payload, args }) =>
        flexMessageTemplateGenerator(
          args,
          payload as ChatbotMessagePayload,
          api,
        ),
      ios: ({ args, payload, api }) =>
        iosGenericTemplate({
          alert: flexMessageTemplateGenerator(
            args,
            payload as ChatbotMessagePayload,
            api,
          ).altText,
          payload,
        }),
      android: ({ args, payload, api }) =>
        androidGenericTemplate({
          title: flexMessageTemplateGenerator(
            args,
            payload as ChatbotMessagePayload,
            api,
          ).altText,
          payload,
        }),
    },
  });
}

/**
 * Create flex container
 */
export function createFlexContainer(
  altText: string,
  container: FlexContainer,
): FlexMessage {
  return {
    type: 'flex',
    altText,
    contents: container,
  };
}

/**
 * Create flex carousel container
 */
export function createCarouselContainer(bubbles: FlexBubble[]): FlexCarousel {
  return {
    type: 'carousel',
    contents: bubbles,
  };
}

/**
 * Create open web url flex message action
 */
export function createOpenWebAction(altText: string, payload: object): Action {
  // Action label limited by 40 characters
  const label = altText.length > 40 ? `${altText.substring(0, 38)}…` : altText;

  return {
    type: 'uri',
    label,
    uri: getOpenWebAppLink(payload),
  };
}

/**
 * Create open game action
 */
export function createOpenGameAction(
  altText: string,
  payload: object & {
    alternativeWebIABBucket: AlternativeOpenWebBuckets;
  },
): Action {
  // Action label limited by 40 characters
  const label = altText.length > 40 ? `${altText.substring(0, 38)}…` : altText;

  return {
    type: 'uri',
    label,
    uri: getLineRedirectURL(payload),
  };
}

/**
 * Get message data for api.chatbot.sendMessage
 */
export function getChatbotFlexMessage(args: ChatbotFlexMessageArgs) {
  const { template, data, payload, analyticsUserProperties } = args;

  return chatbotMessageTemplates[template]({
    args: data as never, // TODO: Fix this type.
    payload,
    analyticsUserProperties,
  }) as ChatbotMessageInstance<typeof data>;
}

/**
 * Get random creative asset from given list with prefix id
 */
export function getRandomCreativeAsset(
  args: {
    prefixId: `${2026 | 2025 | 2024}-${string}-${string}`; // e.g. '2025-02-18' Year-Month-Day
    best: FlexCreativeAsset[];
    worst?: FlexCreativeAsset[];
  },
  random: () => number,
): FlexCreativeAsset {
  const { best, worst } = args;

  // 70% chance to pick the best performers
  // If we have no worst performers, use the best
  const choices = random() >= 0.3 || !worst ? best : worst;
  const creative = getRandomItemFromArray(choices, random);

  return {
    ...creative,
    id: args.prefixId + '-' + creative.id, // Override id with prefix
  };
}
